import { api } from './api';
import type {
  VaultSettings, VaultItem, VaultStats, VaultFilters, VaultQueueStatus, TensionCategory } from '../types';

export const vaultApi = {
  addByPostId: (postId: number, strategyId?: number) =>
    api.post<{ item_id?: number; existing?: boolean }>('/vault/add', {
      post_id: postId,
      strategy_id: strategyId,
    }),

  addByUrl: (url: string, strategyId?: number) =>
    api.post<{ item_id?: number; existing?: boolean; needs_scrape?: boolean; url?: string }>(
      '/vault/add-by-url',
      { url, strategy_id: strategyId }
    ),

  scrapeAndAdd: (url: string, strategyId?: number) =>
    api.post<{ item_id: number; status: string }>('/vault/scrape-and-add', {
      url,
      strategy_id: strategyId,
    }),

  discoverArticles: (url: string, signal?: AbortSignal) =>
    api.post<{
      count: number;
      truncated: boolean;
      sample: { url: string; title: string | null }[];
      article_urls: string[];
      estimated_cost_usd: number;
      used_apify_probe: boolean;
      probe_cost_usd: number | null;
    }>('/vault/discover-articles', { url }, { signal }),

  importArticles: (articleUrls: string[], strategyId?: number) =>
    api.post<{ neu: number; bekannt: number; uebersprungen: number; item_ids: number[] }>(
      '/vault/import-articles',
      { article_urls: articleUrls, strategy_id: strategyId }
    ),

  listItems: (filters: VaultFilters) => {
    const params = new URLSearchParams();
    if (filters.review_status) params.set('review_status', filters.review_status);
    if (filters.is_favorite != null) params.set('is_favorite', String(filters.is_favorite));
    if (filters.is_shortlisted != null) params.set('is_shortlisted', String(filters.is_shortlisted));
    if (filters.strategy_id != null) params.set('strategy_id', String(filters.strategy_id));
    if (filters.format_type) params.set('format_type', filters.format_type);
    if (filters.tension_category) params.set('tension_category', filters.tension_category);
    if (filters.tag) params.set('tag', filters.tag);
    if (filters.platform) params.set('platform', filters.platform);
    if (filters.only_original) params.set('only_original', String(filters.only_original));
    if (filters.is_reply != null) params.set('is_reply', String(filters.is_reply));
    params.set('sort', filters.sort);
    params.set('limit', String(filters.limit));
    params.set('offset', String(filters.offset));
    return api.get<{ items: VaultItem[]; total: number }>(`/vault/items?${params}`);
  },

  getItem: (itemId: number) => api.get<VaultItem>(`/vault/items/${itemId}`),

  updateItem: (itemId: number, updates: Partial<VaultItem>) =>
    api.patch<VaultItem>(`/vault/items/${itemId}`, updates),

  deleteItem: (itemId: number) => api.delete<void>(`/vault/items/${itemId}`),

  getStats: () => api.get<VaultStats>('/vault/stats'),

  getQueue: () => api.get<VaultQueueStatus>('/vault/queue'),

  getTensions: () => api.get<TensionCategory[]>('/vault/tensions'),

  getSettings: () => api.get<VaultSettings>('/vault/settings'),

  updateSettings: (settings: VaultSettings) =>
    api.patch<VaultSettings>('/vault/settings', settings),
};
